/**
 * The conductor's desk: plan, read, approve.
 *
 * The conductor plans when an operator asks and acts when they approve — no
 * timer, no autonomous loop (ADR-0016). This panel is the asking and the
 * approving. "Şefi çalıştır" builds a plan and shows it: which task would start
 * on which account and why, what it could not start, and what it would reclaim.
 * Approving hands it to the server, which drives the sessions the way the
 * cockpit does and writes each outcome back onto the run — so this panel keeps
 * reading it while it runs.
 *
 * A plan spends real quota, so nothing here acts on its own: the plan is
 * inspected, and the approve button is the one deliberate step between a
 * proposal and a running agent.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Play } from 'lucide-react'

import { Icon } from '@/components/icon'
import { Badge, Button, Panel, StatusDot } from '@/components/primitives'
import { Notice, useApiErrorMessage } from '@/components/controls'
import { useConfirm } from '@/components/confirm'
import { useT, useLocale } from '@/i18n/context'
import { formatRelative } from '@/i18n'
import { runStateTone, startOutcomeTone } from '@/lib/tone'
import { useConductorMutations, useConductorRuns } from './workData'
import type { ConductorRun, ID } from '@/types/domain'
import './ConductorPanel.css'

export function ConductorPanel({ taskTitle }: { taskTitle: (id: ID) => string }) {
  const t = useT()
  const { locale } = useLocale()
  const describe = useApiErrorMessage()
  const confirm = useConfirm()
  const runs = useConductorRuns()
  const { plan, approve } = useConductorMutations()
  const [planned, setPlanned] = useState<ConductorRun | null>(null)

  const latest = runs.data?.items?.[0]
  const shown = planned ?? (latest && latest.state === 'PLANNED' ? latest : null)
  const running = runs.data?.items?.find((r) => r.state === 'APPROVED')

  const doPlan = () =>
    plan.mutate(undefined, {
      onSuccess: (run) => setPlanned(run),
    })

  const doApprove = async (run: ConductorRun) => {
    const ok = await confirm({
      title: t('conductor.approve'),
      message: t('conductor.approveConfirm', { count: run.starts.length }),
      confirmLabel: t('conductor.approve'),
      tone: 'default',
    })
    if (!ok) return
    approve.mutate(run.id, { onSuccess: () => setPlanned(null) })
  }

  const runButton = (
    <Button size="sm" variant="primary" busy={plan.isPending} onClick={doPlan} disabled={Boolean(running)}>
      <Icon icon={Play} size={14} />
      {t('conductor.run')}
    </Button>
  )

  // Nothing planned, nothing running, nothing ever run, nothing wrong. A titled
  // panel whose whole body is a sentence explaining what the feature is takes a
  // hundred and ten pixels above the work to say that nothing has happened —
  // which is decoration (CLAUDE.md). The sentence still earns its line, because
  // it is the safety claim: the chief starts nothing by itself. So it is a line,
  // beside the button that would change that.
  const idle =
    !running && !shown && !plan.error && !approve.error && (runs.data?.items?.length ?? 0) === 0

  if (idle) {
    return (
      <div className="k-cond__idle">
        <span className="k-cond__lede">{t('conductor.lede')}</span>
        {runButton}
      </div>
    )
  }

  return (
    <Panel title={t('conductor.title')} className="k-cond" actions={runButton}>
      <p className="k-cond__lede">{t('conductor.lede')}</p>
      {(plan.error || approve.error) && <Notice tone="err">{describe(plan.error ?? approve.error).title}</Notice>}

      {running && (
        <Notice tone="info">
          {t('conductor.applying', { count: running.results?.length ?? 0, total: running.starts.length })}{' '}
          <Link to="#" className="k-cond__link" onClick={(e) => e.preventDefault()}>
            {running.starts.map((s) => taskTitle(s.taskId)).join(', ')}
          </Link>
        </Notice>
      )}

      {shown && (
        <div className="k-cond__plan">
          {shown.starts.length === 0 && shown.reclaims?.length === 0 && (
            <p className="k-cond__empty">{t('conductor.nothingToStart')}</p>
          )}
          {shown.starts.length > 0 && (
            <>
              <h3 className="k-label">{t('conductor.starts', { count: shown.starts.length })}</h3>
              <ul className="k-cond__list">
                {shown.starts.map((s) => (
                  <li key={s.taskId} className="k-cond__start">
                    <span className="k-cond__rank k-mono">#{s.rank + 1}</span>
                    <Link to={`/tasks/${s.taskId}`} className="k-cond__task">
                      {taskTitle(s.taskId)}
                    </Link>
                    {s.reasons && s.reasons.length > 0 && (
                      <span className="k-cond__why">{s.reasons.join(' · ')}</span>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
          {shown.reclaims && shown.reclaims.length > 0 && (
            <p className="k-cond__reclaim">{t('conductor.reclaims', { count: shown.reclaims.length })}</p>
          )}
          {shown.deferrals && shown.deferrals.length > 0 && (
            <p className="k-cond__deferred">{t('conductor.deferred', { count: shown.deferrals.length })}</p>
          )}
          {shown.state === 'PLANNED' && shown.starts.length > 0 && (
            <Button variant="primary" busy={approve.isPending} onClick={() => void doApprove(shown)}>
              {t('conductor.approve')}
            </Button>
          )}
        </div>
      )}

      {(runs.data?.items?.length ?? 0) > 0 && (
        <details className="k-cond__history">
          <summary className="k-cond__historysum">{t('conductor.history')}</summary>
          <ul className="k-cond__runs">
            {runs.data!.items.slice(0, 8).map((r) => (
              <li key={r.id} className="k-cond__runrow">
                <StatusDot tone={runStateTone(r.state)} label={r.state} />
                <span className="k-cond__runmeta">
                  {t('conductor.runSummary', { starts: r.starts.length })}
                  <span className="k-mono"> · {formatRelative(r.plannedAt, locale)}</span>
                </span>
                {r.results && r.results.length > 0 && (
                  <span className="k-cond__outcomes">
                    {r.results.map((res, i) => (
                      <Badge key={i} tone={startOutcomeTone(res.outcome)} subtle mono>
                        {res.outcome}
                      </Badge>
                    ))}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Panel>
  )
}
